import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';
import * as staticPaths from '../../staticPaths';
import HeaderNav from '../HeaderNav';

const contentDefault = {
  title: (
    <>
      <span className="text-emerald-600">Seating Chart Maker</span>
      <br />
      Sketch, Set, Sit — Plan Online
    </>
  ),
  description: (
    <>
      <span className="font-medium text-gray-900">
        Need a seating chart fast?
      </span>{' '}
      Set up your classroom or event in just{' '}
      <span className="font-medium text-gray-900">3 minutes</span>. Turn a list
      of names into a dynamic, shareable seating plan. Use our customizable
      templates or create your unique floor plan.
    </>
  ),
  cta: 'Start creating seating charts now',
  ctaSub: 'Free trial. No credit card required.',
  heroImg: (
    <StaticImage
      data-test="hero-img"
      className="relative rounded-lg shadow-lg"
      src="../../images/landing/hero/generic-04-24.png"
      alt="A screenshot of a seating chart being created with the Seating Chart Maker. The room has 31 seats in different types of table groupings."
      loading="eager"
      width={976}
    />
  ),
  statistic: (
    <>
      Used by <span className="text-lg text-gray-300">2000+</span> educators and
      event planners.
    </>
  ),
};

type Content = typeof contentDefault;

const contentClassroom: Content = {
  title: (
    <>
      <span className="text-emerald-600">Seating Chart Maker</span>
      <br />
      Generate Classroom Seating Plans
    </>
  ),
  description: (
    <>
      <span className="font-medium text-gray-900">
        Need a seating chart fast?
      </span>{' '}
      Set up your classroom in just{' '}
      <span className="font-medium text-gray-900">3 minutes</span>. Turn a list
      of student names into a seating plan—randomize, share, or adjust as
      needed. Use our customizable templates or create your unique classroom
      floor plan.
    </>
  ),
  cta: 'Start creating seating charts now',
  ctaSub: 'Free trial. No credit card required.',
  heroImg: (
    <StaticImage
      data-test="hero-img"
      className="relative rounded-lg shadow-lg"
      src="../../images/landing/classroom-hero-04-24.png"
      alt="A screenshot of a school seating chart being generated in Seating Chart Maker. The classroom has 25 seats in different kind of table groups. The classroom is a simple rectangle with a whiteboard at the front, with a wooden floor and a decorative plant. Person cards show person names and portraits and visually indicate where the person is seated."
      loading="eager"
      width={976}
    />
  ),
  statistic: (
    <>
      The go-to software for{' '}
      <span className="text-lg text-gray-300">2000+</span> forward-looking
      teachers.
    </>
  ),
};

const contentWedding: Content = {
  title: (
    <>
      <span className="text-emerald-600">Seating Chart Maker</span>
      <br />
      Create Wedding Seating Plans
    </>
  ),
  description: (
    <>
      <span className="font-medium text-gray-900">
        Need a seating chart without stress?
      </span>{' '}
      Set up your event seating in just{' '}
      <span className="font-medium text-gray-900">3 minutes</span>. Turn a list
      of guest names into a dynamic, shareable seating plan. Use our
      customizable templates or create your unique venue floor plan.
    </>
  ),
  cta: 'Start creating seating charts now',
  ctaSub: 'Free trial. No credit card required.',
  heroImg: (
    <StaticImage
      data-test="hero-img"
      className="relative rounded-lg shadow-lg"
      src="../../images/landing/hero/wedding-small-round-04-24.png"
      alt="A screenshot of the Seating Chart Maker software where a wedding seating chart is being created for a reception or dinner event with four different size round tables for 25 guests. The room has a solid color floor, decorative plants and a small buffet table. Person cards show person names and portraits and visually indicate where the person is seated."
      loading="eager"
      width={976}
    />
  ),
  statistic: (
    <>
      The go-to choice for <span className="text-lg text-gray-300">2000+</span>{' '}
      forward-looking event planners.
    </>
  ),
};

const Hero: React.FC<{ content: Content }> = ({ content }) => (
  <div className="bg-gray-50">
    <div className="relative overflow-hidden">
      <div className="absolute inset-y-0 h-full w-full" aria-hidden="true">
        <div className="relative h-full">
          <svg
            className="absolute right-full transform translate-y-1/3 translate-x-1/4 md:translate-y-1/2 sm:translate-x-1/2 lg:translate-x-full"
            width={404}
            height={784}
            fill="none"
            viewBox="0 0 404 784"
          >
            <defs>
              <pattern
                id="e229dbec-10e9-49ee-8ec3-0286ca089edf"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={784}
              fill="url(#e229dbec-10e9-49ee-8ec3-0286ca089edf)"
            />
          </svg>
          <svg
            className="absolute left-full transform -translate-y-3/4 -translate-x-1/4 sm:-translate-x-1/2 md:-translate-y-1/2 lg:-translate-x-3/4"
            width={404}
            height={784}
            fill="none"
            viewBox="0 0 404 784"
          >
            <defs>
              <pattern
                id="d2a68204-c383-44b1-b99f-42ccff4e5365"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={784}
              fill="url(#d2a68204-c383-44b1-b99f-42ccff4e5365)"
            />
          </svg>
        </div>
      </div>

      <div className="relative pt-6 pb-16 sm:pb-24">
        <HeaderNav />
        <div className="mt-16 mx-auto max-w-7xl px-4 sm:mt-24 sm:px-6">
          <div className="text-center">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl xl:text-6xl">
              {content.title}
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-700 bg-gray-50 bg-opacity-50 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              {content.description}
            </p>
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              <div>
                <div className="rounded-md shadow">
                  <Link
                    data-test="hero-cta"
                    to={staticPaths.app}
                    className="w-full flex items-center justify-center px-6 py-2 md:px-8 md:py-3 border border-transparent text-base md:text-lg font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    {content.cta}
                  </Link>
                </div>
                <p className="mt-2 text-xs md:text-sm text-gray-600">
                  {content.ctaSub}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex flex-col" aria-hidden="true">
          <div className="flex-1" />
          <div className="flex-1 w-full bg-gray-800" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">{content.heroImg}</div>
      </div>
    </div>
    <div className="bg-gray-800">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
        <p className="text-center text-gray-400 text-sm font-semibold uppercase tracking-wide">
          {content.statistic}
        </p>
      </div>
    </div>
  </div>
);

export const HeroDefault = () => <Hero content={contentDefault} />;
export const HeroClassroom = () => <Hero content={contentClassroom} />;
export const HeroWedding = () => <Hero content={contentWedding} />;
